import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClipboardEvent, KeyboardEvent } from 'react';
import { useParams } from 'react-router-dom';

import { useValidateCeebCodes } from '../../../hooks/use-aid-calculations';
import { parseCeebCodeTokens } from '../helpers/ceeb-codes';
import type { CeebCodeChip } from '../calculations-step.types';

import { useCurrentUser } from 'hooks/api/use-current-user';

let ceebCodeIdCounter = 0;
const makeCeebCodeId = (): string => `ceeb-code-${(ceebCodeIdCounter += 1)}`;

export type CeebCodesUpdater = (prev: CeebCodeChip[]) => CeebCodeChip[];

interface UseCeebCodesInputParams {
  codes: CeebCodeChip[];
  onCodesChange: (updater: CeebCodesUpdater) => void;
}

export interface UseCeebCodesInputResult {
  draftText: string;
  handleChange: (value: string) => void;
  handlePaste: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
  handleKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  handleBlur: () => void;
  removeCode: (id: string) => void;
  removeAllUnmatched: () => void;
  clearAll: () => void;
  hasPending: boolean;
  matchedCount: number;
  unmatchedCount: number;
  totalCount: number;
}

/**
 * Owns the CEEB codes chip input's interaction + validation orchestration
 * (FR-5–FR-33): parses pasted/typed text into tokens, tracks each chip's own
 * pending/matched/unmatched state, and calls the matching endpoint once per
 * newly-added batch (FR-14) so previously-resolved chips are never re-sent.
 *
 * `codes` / `onCodesChange` are controlled by the caller (the eligibility row's
 * form state) rather than owned here, because `use-special-award-form.ts` needs
 * the chip list to compute row validity (FR-27) and the submitted `values` csv.
 */
export const useCeebCodesInput = ({
  codes,
  onCodesChange,
}: UseCeebCodesInputParams): UseCeebCodesInputResult => {
  const { modelId } = useParams();
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser?.oktaUserId;
  const { mutateAsync: validate } = useValidateCeebCodes();

  const [draftText, setDraftText] = useState('');
  const hasRequestedInitialValidation = useRef(false);

  // Uses the per-call promise (mutateAsync), not `mutate`'s per-call
  // onSuccess/onError options: those route through the mutation's single
  // shared observer, which detaches from the previous in-flight batch as
  // soon as a newer one starts (see @tanstack/query-core's
  // MutationObserver#mutate) — so an earlier batch's response would arrive
  // from the backend but never reach its callback, leaving that batch's
  // chips stuck 'pending' forever when two batches overlap.
  const validateBatch = useCallback(
    (pendingIds: Set<string>, tokens: string[]) => {
      if (!userId || !modelId) {
        return;
      }

      validate({ userId, modelId, ceebCodes: tokens })
        .then((result) => {
          const validSet = new Set(result.valid ?? []);

          onCodesChange((prev) =>
            prev.map((chip) =>
              pendingIds.has(chip.id)
                ? {
                    ...chip,
                    status: validSet.has(chip.code) ? 'matched' : 'unmatched',
                  }
                : chip
            )
          );
        })
        .catch(() => {
          onCodesChange((prev) =>
            prev.map((chip) =>
              pendingIds.has(chip.id) ? { ...chip, status: 'unmatched' } : chip
            )
          );
        });
    },
    [userId, modelId, validate, onCodesChange]
  );

  const commitTokens = useCallback(
    (text: string) => {
      const tokens = parseCeebCodeTokens(text);

      if (!tokens.length) {
        return;
      }

      const pendingChips: CeebCodeChip[] = tokens.map((code) => ({
        id: makeCeebCodeId(),
        code,
        status: 'pending',
      }));

      onCodesChange((prev) => [...prev, ...pendingChips]);

      if (!userId || !modelId) {
        return;
      }

      // Keeps the mount-seed effect below from re-firing once validation runs.
      hasRequestedInitialValidation.current = true;
      validateBatch(new Set(pendingChips.map((chip) => chip.id)), tokens);
    },
    [onCodesChange, validateBatch, userId, modelId]
  );

  // FR-32: opening Edit re-validates every previously-saved code live rather
  // than replaying its last-saved match state, so a prefilled row starts every
  // chip as 'pending' (see `buildEligibilityRows`) and this fires once to
  // resolve them. Guarded by a ref (not just the "any pending?" check) so a
  // parent re-render mid-flight can't fire a second concurrent request for the
  // same batch.
  useEffect(() => {
    if (hasRequestedInitialValidation.current) {
      return;
    }

    const initialPending = codes.filter((chip) => chip.status === 'pending');

    if (!initialPending.length || !userId || !modelId) {
      return;
    }

    hasRequestedInitialValidation.current = true;
    validateBatch(
      new Set(initialPending.map((chip) => chip.id)),
      initialPending.map((chip) => chip.code)
    );
  }, [codes, userId, modelId, validateBatch]);

  const handleChange = useCallback(
    (value: string) => {
      const lastChar = value.slice(-1);

      if (lastChar === ',' || lastChar === ' ') {
        const completed = value.slice(0, -1);

        if (completed.trim()) {
          commitTokens(completed);
          setDraftText('');

          return;
        }
      }

      setDraftText(value);
    },
    [commitTokens]
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      event.preventDefault();

      const { selectionStart, selectionEnd } = event.currentTarget;
      const before = draftText.slice(0, selectionStart ?? draftText.length);
      const after = draftText.slice(selectionEnd ?? draftText.length);

      commitTokens(`${before}${event.clipboardData.getData('text')}${after}`);
      setDraftText('');
    },
    [draftText, commitTokens]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();

      if (draftText.trim()) {
        commitTokens(draftText);
        setDraftText('');
      }
    },
    [draftText, commitTokens]
  );

  const handleBlur = useCallback(() => {
    if (draftText.trim()) {
      commitTokens(draftText);
    }

    setDraftText('');
  }, [draftText, commitTokens]);

  const removeCode = useCallback(
    (id: string) =>
      onCodesChange((prev) => prev.filter((chip) => chip.id !== id)),
    [onCodesChange]
  );

  const removeAllUnmatched = useCallback(
    () =>
      onCodesChange((prev) =>
        prev.filter((chip) => chip.status !== 'unmatched')
      ),
    [onCodesChange]
  );

  const clearAll = useCallback(() => {
    onCodesChange(() => []);
    setDraftText('');
  }, [onCodesChange]);

  return {
    draftText,
    handleChange,
    handlePaste,
    handleKeyDown,
    handleBlur,
    removeCode,
    removeAllUnmatched,
    clearAll,
    hasPending: codes.some((chip) => chip.status === 'pending'),
    matchedCount: codes.filter((chip) => chip.status === 'matched').length,
    unmatchedCount: codes.filter((chip) => chip.status === 'unmatched').length,
    totalCount: codes.length,
  };
};
